import { CategoriesWithRules, CategoriesWithMultiSelection } from './reservation-types';
import { ReservationRequestItem } from './reservation-request.model';
import { ReservationServiceItem } from './reservation-service.model';

import * as lodash from 'lodash';

export interface IReservationServiceCategory {
  categoryId?: string;
  categoryName?: string;
  coreServices?: ReservationServiceItem[];
  addonServices?: ReservationServiceItem[];
  multiSelectable?: boolean;
}

export class ReservationServiceCategory {
  // #region -> Property <-
  // ----------

  categoryId: string;
  categoryName: string;

  /**
   * Core Services under this category.
   */
  coreServices: ReservationServiceItem[] = [];

  /**
   * Add-on Services under this category.
   */
  addonServices: ReservationServiceItem[] = [];

  /**
   * If the core services under this category can be multi-selectable.
   *
   * By default, it is `false`.
   *
   */
  multiSelectable = false;

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Constructor <-
  // ----------

  constructor(newItem: IReservationServiceCategory) {
    this.categoryId = newItem.categoryId || undefined;
    this.categoryName = newItem.categoryName || undefined;
    this.coreServices = newItem.coreServices || [];
    this.addonServices = newItem.addonServices || [];
    this.multiSelectable = newItem.multiSelectable || false;

    // Check if the category can be multi-selectable
    this.multiSelectable = this._determineIfMultiSelection();
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Method - Immutable <-
  // ----------

  json(): IReservationServiceCategory {
    //
    return lodash.cloneDeep({
      categoryId: this.categoryId,
      categoryName: this.categoryName,
      coreServices: this.coreServices,
      addonServices: this.addonServices,
      multiSelectable: this.multiSelectable,
    });
  }

  clone(): ReservationServiceCategory {
    //
    return new ReservationServiceCategory(this.json());
  }

  update(updatedData: Partial<IReservationServiceCategory>): ReservationServiceCategory {
    //
    const newData: IReservationServiceCategory = {...this.json(), ...updatedData};

    // Return a new reference
    return new ReservationServiceCategory(newData);
  }

  // ----------
  // #endregion

  /////////////////////////////


  // #region -> Method - Get <-
  // ----------

  hasCoreServices(): boolean {
    return this.coreServices.length > 0;
  }

  hasAddonServices(): boolean {
    return this.addonServices.length > 0;
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Method - Get - Rule Definition <-
  // ----------

  /**
   *
   * @param request ReservationRequestItem The Current "reservation request" object which includes the
   * selections of "core services".
   * The current selections of "core services" would affect other categories.
   *
   * @returns boolean If this category can be selectable.
   */
  canBeSelectable(request: ReservationRequestItem): boolean {
    //
    if (!request) {
      // No request yet, always "selectable"
      return true;
    }

    // All "false" rules.
    // Rule 1 - Category “Day Care”, “Boarding” and “Training” are exclusive.
    const exclusiveListForDayCareBoardingTraining = [
      CategoriesWithRules.DayCare,
      CategoriesWithRules.Boarding,
      CategoriesWithRules.Training,
    ];
    if (
      !this._canBeAddedBasedOnExclusiveList(
        this.categoryId,
        request.coreCategoryIds,
        exclusiveListForDayCareBoardingTraining
      )
    ) {
      return false;
    }

    // Rule 2 - Category “Full Service Grooming“ and “Other Grooming” are exclusive.
    const exclusiveListForGroomingFSG = [
      CategoriesWithRules.Grooming,
      CategoriesWithRules.FullServiceGrooming,
    ];
    if (
      !this._canBeAddedBasedOnExclusiveList(
        this.categoryId,
        request.coreCategoryIds,
        exclusiveListForGroomingFSG
      )
    ) {
      return false;
    }

    // Rule 3 - If chosen "Training", cannot select Others
    if (
      this.categoryId !== CategoriesWithRules.Training &&
      request.includeCoreCategory(CategoriesWithRules.Training)
    ) {
      // "Training" is in, no selection
      return false;
    }
    if (
      this.categoryId === CategoriesWithRules.Training &&
      !request.includeCoreCategory(CategoriesWithRules.Training) &&
      request.hasCoreServices()
    ) {
      // Cannot select "Training" if having others
      return false;
    }


    // Final fallback -  always "selectable"
    return true;
  }

  availableAddonServices(
    request: ReservationRequestItem
  ): ReservationServiceItem[] {
    //
    // Rule - If the "core service" includes "Training", no add-ons!!
    if (request.includeCoreCategory(CategoriesWithRules.Training)) {
      return [];
    }

    // Rule - If the "core service" is ONLY for "Full Service Grooming", no add-ons!!
    if (request.coreCategoryIds.length === 1 &&
      request.includeCoreCategory(CategoriesWithRules.FullServiceGrooming)) {
      return [];
    }

    // Rule - If the "core service" includes "Full Service Grooming",
    //  no add-ons for "Bath" and "Other Grooming"
    if (
      request.includeCoreCategory(CategoriesWithRules.FullServiceGrooming) &&
      (this.categoryId === CategoriesWithRules.Bath ||
        this.categoryId === CategoriesWithRules.Grooming)
    ) {
      return [];
    }

    // Default - return "All"
    return this.addonServices;
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Method - Set <-
  // ----------

  addNewService(newService: ReservationServiceItem): ReservationServiceCategory {
    //
    if (newService.isPrimary) {
      // Core
      this.coreServices.push(newService);
    } else {
      this.addonServices.push(newService);
    }

    //
    return this.clone();
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Private Method <-
  // ----------

  /**
   * Determine if the services under this category can be multi-selectable.
   */
  private _determineIfMultiSelection(): boolean {
    //
    if (
      CategoriesWithMultiSelection.includes(
        this.categoryId as CategoriesWithRules
      )
    ) {
      //
      return true;
    }

    //
    return false;
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Private Method <-
  // ----------

  /**
   * Helper - check if an item can be added into the "target list" based on a rule of "exclusive list".
   *
   * Based on the rule:
   * - This item can be added only if the "target list" does not have any "other" item from the "exclusive list".
   * - If the "target list" already includes this item, it is till treated as "can".
   *
   * @param itemToBeAdded The item to be added.
   * @param targetList The target list to accept new item.
   * @param exclusiveList The definition of "exclusion relationship".
   */
  private _canBeAddedBasedOnExclusiveList(
    itemToBeAdded: any,
    targetList: any[],
    exclusiveList: any[]
  ): boolean {
    //
    if (targetList.includes(itemToBeAdded)) {
      // Already in, can add for sure.
      return true;
    }

    if (!exclusiveList.includes(itemToBeAdded)) {
      // Not included in the exclusive list, ignore
      return true;
    }

    // Check if there is already some item from the exclusive list
    const intersectionList = targetList.filter((item) =>
      exclusiveList.includes(item)
    );
    if (intersectionList.length === 0) {
      // No other exists, can add
      return true;
    }

    //
    return false;
  }

  // ----------
  // #endregion

  /////////////////////////////

}
